import PropTypes from "prop-types";

/**
 * RealisticBlowerFan – High-fidelity 3D centrifugal blower/fan component.
 * Features:
 * - Perfectly centered 3D impeller wheel rotation on true axis (cx: 175.5, cy: 211.5)
 * - Tightly cropped 418x381 viewBox without wasted space
 * - Zero wobbling/eccentricity and zero double-wheel ghosting
 * - Clean exhaust airflow stream & wind wave gusts at discharge port
 * - Mounting eyelet status LED
 */
const RealisticBlowerFan = ({
  isRunning,
  isOffline,
  statusColor,
  speedScale = 1,
  opacity = 1,
  width = "100%",
  height = "100%",
}) => {
  const dotColor = statusColor === "green" ? "#22c55e" : statusColor === "red" ? "#ef4444" : "#94a3b8";
  const spinDuration = isRunning ? `${Math.max(0.25, 0.9 / (speedScale || 1))}s` : "0s";

  return (
    <div
      className={`scada-realistic-container ${isOffline ? "scada-realistic--offline" : ""}`}
      style={{
        width,
        height,
        opacity,
      }}
    >
      {/* 3D Realistic Base Housing */}
      <img
        src="/scada-realistic/blower.png"
        alt="SCADA Realistic Blower Fan"
        className="scada-realistic-img"
      />

      {/* Dynamic Animated Overlay (SVG synchronized in 418x381 space) */}
      <svg
        className="scada-realistic-overlay"
        viewBox="0 0 418 381"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          {/* Radial gradient matching the recessed cavity behind the impeller */}
          <radialGradient id="blowerCavityGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#373a46" />
            <stop offset="70%" stopColor="#252730" />
            <stop offset="100%" stopColor="#1a1c22" />
          </radialGradient>

          {/* Clip path centered exactly on true rotation axis (175.5, 211.5) */}
          <clipPath id="blowerImpellerClip">
            <circle cx="175.5" cy="211.5" r="86" />
          </clipPath>

          <filter id="blowerLedGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* ── Background Cavity Mask ── */}
        <circle cx="175.5" cy="211.5" r="86" fill="url(#blowerCavityGrad)" />

        {/* ── Single Authentic Impeller Wheel Rotating on True Center (175.5px, 211.5px) ── */}
        <g
          clipPath="url(#blowerImpellerClip)"
          style={{
            transformOrigin: "175.5px 211.5px",
            animation: isRunning ? `scada-spin ${spinDuration} linear infinite` : "none",
          }}
        >
          <image
            href="/scada-realistic/blower.png"
            x="0"
            y="0"
            width="418"
            height="381"
          />
        </g>

        {/* ── Exhaust Airflow Gusts (Discharge Port at top-right) ── */}
        {isRunning && (
          <g>
            <path
              d="M 385 63 Q 415 48 435 38"
              stroke="#38bdf8"
              strokeWidth="3.5"
              strokeDasharray="10 8"
              strokeLinecap="round"
              opacity="0.75"
              style={{ animation: "scada-exhaust-wind 1.2s infinite ease-out" }}
            />
            <path
              d="M 385 113 Q 420 98 440 88"
              stroke="#38bdf8"
              strokeWidth="3.5"
              strokeDasharray="10 8"
              strokeLinecap="round"
              opacity="0.75"
              style={{ animation: "scada-exhaust-wind 1.2s infinite 0.4s ease-out" }}
            />
          </g>
        )}

        {/* ── Top-Left Mounting Status LED ── */}
        <circle
          cx="17"
          cy="18"
          r="4.5"
          fill={dotColor}
          stroke="#ffffff"
          strokeWidth="1.2"
          filter={isRunning ? "url(#blowerLedGlow)" : "none"}
          style={{
            animation: isRunning ? "scada-led-blink 2s infinite ease-in-out" : "none",
          }}
        />
      </svg>
    </div>
  );
};

RealisticBlowerFan.propTypes = {
  liveValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  unit: PropTypes.string,
  isRunning: PropTypes.bool,
  isOffline: PropTypes.bool,
  statusColor: PropTypes.string,
  speedScale: PropTypes.number,
  opacity: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
};

export default RealisticBlowerFan;
